import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import { IoSearchOutline } from "react-icons/io5";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { FaLocationDot, FaStar } from "react-icons/fa6";
import { PiSealCheckFill } from "react-icons/pi";
import ReviewAvatar from "@/Component/Common/CustomerReviews/ReviewAvatar";
import useIsDesktop from "@/hooks/useIsDesktop";
import useSlickArrows from "@/hooks/useSlickArrows";
import {
  buildMobileDestinationSlides,
} from "./destinationCarousel";
import styles from "./SmartSearchModal.module.css";

const DESKTOP_SLIDES_TO_SHOW = 4;

function LoadingBlock({ className = "" }) {
  return (
    <div className={`${styles.loadingWrap} ${className}`.trim()}>
      <span className={styles.spinner} role="status" aria-label="Loading" />
    </div>
  );
}

function DestinationCard({ item, isSelected, onSelect, variant = "desktop" }) {
  return (
    <button
      type="button"
      data-dest-carousel-card
      className={`${styles.destCard} ${styles[`destCard${variant === "mobile" ? "Mobile" : "Desktop"}`]} ${variant === "desktop" && isSelected ? styles.destCardActive : ""
        }`.trim()}
      onClick={onSelect}
    >
      {item.image ? (
        <img src={item.image} alt={item.label} className={styles.destImage} />
      ) : (
        <div className={styles.destImage} />
      )}
      <span className={styles.destLabel}>{item.label}</span>
    </button>
  );
}

export default function SmartSearchStepDestination({
  selection,
  onSelectDestination,
  searchQuery,
  setSearchQuery,
  showSuggestions,
  setShowSuggestions,
  searchResults,
  searchLoading = false,
  destinations,
  destinationsLoading,
  reviewsLoading,
  activeReview,
}) {
  const carouselRef = useRef(null);
  const desktopSliderRef = useRef(null);
  const { isDesktop, mounted } = useIsDesktop(900);
  const isMobileCarousel = mounted && !isDesktop;
  const [centerDomIndex, setCenterDomIndex] = useState(-1);

  const mobileSlides = useMemo(
    () => (destinations.length ? buildMobileDestinationSlides(destinations) : []),
    [destinations]
  );

  const { canPrev, canNext, slickArrowEvents } = useSlickArrows(
    destinations.length,
    DESKTOP_SLIDES_TO_SHOW,
    destinations.length
  );

  const fewDesktopSlides = destinations.length <= DESKTOP_SLIDES_TO_SHOW;

  const desktopSlickSettings = useMemo(
    () => ({
      dots: false,
      infinite: false,
      centerMode: false,
      speed: 400,
      slidesToShow: DESKTOP_SLIDES_TO_SHOW,
      slidesToScroll: 1,
      arrows: false,
      swipe: false,
      draggable: false,
      touchMove: false,
      ...slickArrowEvents,
      responsive: [
        {
          breakpoint: 1200,
          settings: {
            slidesToShow: 3,
            centerMode: false,
            swipe: false,
            draggable: false,
            touchMove: false,
          },
        },
      ],
    }),
    [slickArrowEvents]
  );

  const getMobileShells = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return [];
    return Array.from(el.querySelectorAll("[data-dest-shell]"));
  }, []);

  const getShellCenterInScroller = useCallback((shell) => {
    return shell.offsetLeft + shell.offsetWidth / 2;
  }, []);

  const syncCenterDomIndex = useCallback(() => {
    const el = carouselRef.current;
    const shells = getMobileShells();
    if (!el || !shells.length) return;

    const scrollCenter = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;

    shells.forEach((child, i) => {
      const childCenter = getShellCenterInScroller(child);
      const dist = Math.abs(scrollCenter - childCenter);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });

    setCenterDomIndex((prev) => (prev === best ? prev : best));
  }, [getMobileShells, getShellCenterInScroller]);

  useLayoutEffect(() => {
    const el = carouselRef.current;
    if (!el || !isMobileCarousel) {
      if (el) el.style.removeProperty("--carousel-edge-pad");
      return undefined;
    }

    const updatePadding = () => {
      const shells = getMobileShells();
      const shell = shells[0];
      if (!shell) return;
      const pad = Math.max(0, (el.clientWidth - shell.offsetWidth) / 2);
      el.style.setProperty("--carousel-edge-pad", `${pad}px`);
      syncCenterDomIndex();
    };

    updatePadding();
    const ro = new ResizeObserver(updatePadding);
    ro.observe(el);
    const first = getMobileShells()[0];
    if (first) ro.observe(first);
    return () => ro.disconnect();
  }, [
    isMobileCarousel,
    destinations.length,
    destinationsLoading,
    getMobileShells,
    syncCenterDomIndex,
  ]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el || !isMobileCarousel) return undefined;

    let raf = 0;
    const onScroll = () => {
      if (raf) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(syncCenterDomIndex);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("scrollend", syncCenterDomIndex, { passive: true });

    raf = window.requestAnimationFrame(syncCenterDomIndex);

    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("scrollend", syncCenterDomIndex);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [isMobileCarousel, mobileSlides.length, syncCenterDomIndex]);

  useEffect(() => {
    if (!isMobileCarousel) return undefined;
    setCenterDomIndex(-1);
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(syncCenterDomIndex);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isMobileCarousel, mobileSlides.length, syncCenterDomIndex]);

  const selectDestination = (item, options) => {
    onSelectDestination?.(item, options);
  };

  const submitSearchSelection = () => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return;

    const match =
      searchResults.find((item) => item.label?.toLowerCase() === query) ||
      searchResults[0] ||
      destinations.find((item) => item.label?.toLowerCase() === query);

    if (!match) return;

    selectDestination(match, { fromSearch: true });
    setSearchQuery(match.label);
    setShowSuggestions(false);
  };

  return (
    <>
      {selection.destination ? (
        <div className={styles.chips}>
          <span className={styles.chip}>
            <FaLocationDot size={10} />
            {selection.destination.label}
          </span>
        </div>
      ) : null}

      <h1 className={styles.title}>
        Where Do You <span className={styles.titleAccent}>Want To Go?</span>
      </h1>

      <div className={styles.searchWrap}>
        <input
          className={styles.searchInput}
          placeholder="Search destination"
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => {
            window.setTimeout(() => setShowSuggestions(false), 150);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            submitSearchSelection();
          }}
        />
        <button
          type="button"
          className={styles.searchIcon}
          aria-label="Search destination"
          onClick={submitSearchSelection}
        >
          <IoSearchOutline size={22} />
        </button>
        {showSuggestions && searchQuery.trim().length >= 2 ? (
          <div className={styles.suggestions}>
            {searchLoading ? (
              <div className={styles.suggestionStatus}>
                <span className={styles.spinnerSm} role="status" aria-label="Loading" />
              </div>
            ) : searchResults.length ? (
              searchResults.map((item) => (
                <button
                  key={`${item.id}-${item.label}`}
                  type="button"
                  className={styles.suggestionItem}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectDestination(item, { fromSearch: true });
                    setSearchQuery(item.label);
                    setShowSuggestions(false);
                  }}
                >
                  {item.label}
                </button>
              ))
            ) : (
              <p className={styles.suggestionEmpty}>No data found</p>
            )}
          </div>
        ) : null}
      </div>

      <div
        className={`${styles.carouselWrap} ${isMobileCarousel ? styles.carouselWrapMobile : styles.carouselWrapDesktop
          }`.trim()}
      >
        {destinationsLoading ? (
          <LoadingBlock />
        ) : mounted && isDesktop ? (
          <div
            className={`${styles.destSlickWrap} ${fewDesktopSlides ? styles.destSlickAlignLeft : ""
              }`.trim()}
            key={`dest-slider-${destinations.length}`}
          >
            {!fewDesktopSlides ? (
              <>
            <button
              type="button"
              className={`headerArrowBtn ${styles.destSlickPrev}`}
              aria-label="Previous destinations"
              disabled={!canPrev}
              onClick={() => desktopSliderRef.current?.slickPrev?.()}
            >
              <MdChevronLeft size={22} />
            </button>
            <button
              type="button"
              className={`headerArrowBtn ${styles.destSlickNext}`}
              aria-label="Next destinations"
              disabled={!canNext}
              onClick={() => desktopSliderRef.current?.slickNext?.()}
            >
              <MdChevronRight size={22} />
            </button>
              </>
            ) : null}
            <Slider ref={desktopSliderRef} {...desktopSlickSettings}>
              {destinations.map((item) => {
                const isSelected = selection.destination?.id === item.id;
                return (
                  <div key={`${item.id}-${item.label}`} className={styles.destSlickSlide}>
                    <DestinationCard
                      item={item}
                      isSelected={isSelected}
                      variant="desktop"
                      onSelect={() => selectDestination(item)}
                    />
                  </div>
                );
              })}
            </Slider>
          </div>
        ) : (
          <div ref={carouselRef} className={styles.carousel}>
            {mobileSlides.map((slide, domIndex) => {
              const { item, key } = slide;
              const isSelected = selection.destination?.id === item.id;
              const isCenter =
                centerDomIndex >= 0 && centerDomIndex === domIndex;
              const shellClass = isCenter
                ? styles.destShellCenter
                : styles.destShellSide;

              return (
                <div
                  key={key}
                  data-dest-shell
                  className={`${styles.destCardMobileShell} ${shellClass}`.trim()}
                >
                  <DestinationCard
                    item={item}
                    isSelected={isSelected}
                    variant="mobile"
                    onSelect={() => {
                      selectDestination(item);
                      // getMobileShells()[domIndex]?.scrollIntoView({
                      //   inline: "center",
                      //   behavior: "smooth",
                      // });
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {reviewsLoading ? (
        <LoadingBlock className={styles.reviewLoading} />
      ) : activeReview ? (
        <>
          <div className={styles.reviewBar}>
            <ReviewAvatar
              imageUrl={activeReview.userImageUrl}
              name={activeReview.userName}
              className={styles.reviewAvatar}
              size={44}
            />
            <div className={styles.reviewMeta}>
              <p className={styles.reviewName}>{activeReview.userName}</p>
              {activeReview.tripToCity ? (
                <span className={styles.reviewTripBadge}>
                  <PiSealCheckFill aria-hidden />
                  <span>Trip To {activeReview.tripToCity}</span>
                </span>
              ) : null}
            </div>
            <div className={styles.VR} />
            <div className={styles.reviewContent}>
              <div className={styles.reviewStars}>
                {Array.from({ length: Math.round(activeReview.rating || 5) }).map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
              <p className={styles.reviewText} title={activeReview.reviewText}>
                {activeReview.reviewText}
              </p>
            </div>
          </div>
          <div className={styles.reviewBarMobile}>
            <div className={styles.reviewBarMobileTop}>
              <ReviewAvatar
                imageUrl={activeReview.userImageUrl}
                name={activeReview.userName}
                className={styles.reviewAvatar}
                size={44}
              />
              <div className={styles.reviewMeta}>
                <p className={styles.reviewName}>{activeReview.userName}</p>
                {activeReview.tripToCity ? (
                  <span className={styles.reviewTripBadge}>
                    <PiSealCheckFill aria-hidden />
                    <span>Trip To {activeReview.tripToCity}</span>
                  </span>
                ) : null}
              </div>
            </div>
            <div className={styles.VR} />
            <div className={styles.reviewContentMobile}>
              <div className={styles.reviewStars}>
                {Array.from({ length: Math.round(activeReview.rating || 5) }).map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
              <p className={styles.reviewText} title={activeReview.reviewText}>
                {activeReview.reviewText}
              </p>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}
